/**
 * Ticket Detail Page
 *
 * Page to view a single ticket with all messages and add replies.
 * Works for both customers and admins.
 * Uses reusable ShadCN UI components and React Query hooks.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTitle } from "../../hooks/useTitle";
import {
  useTicket,
  useReplyToTicket,
  useUpdateTicketStatus,
} from "../../hooks/useTickets";
import { useUser } from "../../hooks/useUser";
import { AdminLayout, useAdminLayout } from "../../components/Layouts/Admin";
import { formatDateLong } from "../../utils/formatDate";
import {
  Card,
  StatusBadge,
  LoadingState,
  ErrorState,
  FormTextarea,
  FormLabel,
  FormError,
  FormSelect,
  PageHeader,
} from "../../components/ui";
import { TicketDetailSkeleton } from "./components/TicketDetailSkeleton";
import { toast } from "react-toastify";

// Helper function to format date for two-line display
const formatDateTwoLines = (dateString) => {
  if (!dateString) return { date: "N/A", time: "" };
  try {
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const timePart = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return { date: datePart, time: timePart };
  } catch {
    return { date: "N/A", time: "" };
  }
};

// Inner content component that can be used with or without AdminLayout
const TicketDetailContent = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: user } = useUser();
  const isAdmin = user?.role === "admin";

  // Check if we're on an admin route
  const isAdminRoute = location.pathname.startsWith("/admin/tickets");

  useTitle(
    isAdminRoute
      ? "Ticket Details - Admin"
      : isAdmin
      ? "Ticket Details"
      : "Support Ticket"
  );

  // Fetch ticket
  const { data: ticket, isLoading, error, refetch } = useTicket(ticketId);

  // Form state for reply
  const [replyMessage, setReplyMessage] = useState("");
  const [replyError, setReplyError] = useState("");

  // Status update state (admin only)
  const [newStatus, setNewStatus] = useState("");

  // Mutations
  const replyMutation = useReplyToTicket();
  const updateStatusMutation = useUpdateTicketStatus();

  // Set initial status when ticket loads
  useEffect(() => {
    if (ticket?.status) {
      setNewStatus(ticket.status);
    } else if (ticket && !ticket.status) {
      // If ticket loaded but no status, default to "open"
      setNewStatus("open");
    }
  }, [ticket]);

  // Get status badge color - memoized to prevent re-creation
  const getStatusColor = useCallback((status) => {
    const colorMap = {
      open: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
      in_progress:
        "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      resolved:
        "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
      closed: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
    };
    return (
      colorMap[status] ||
      "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
    );
  }, []);

  // Sort messages by createdAt
  const sortedMessages = useMemo(() => {
    if (!ticket?.messages) return [];
    return [...ticket.messages].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );
  }, [ticket?.messages]);

  // Handle reply submission - memoized with useCallback
  const handleReply = useCallback(
    async (e) => {
      e.preventDefault();

      if (!replyMessage.trim()) {
        setReplyError("Message is required");
        return;
      }

      if (replyMessage.trim().length < 10) {
        setReplyError("Message must be at least 10 characters");
        return;
      }

      setReplyError("");

      replyMutation.mutate(
        { ticketId, message: replyMessage.trim() },
        {
          onSuccess: () => {
            setReplyMessage("");
            setReplyError("");
            // React Query will automatically invalidate and refetch
          },
        }
      );
    },
    [replyMessage, replyMutation, ticketId]
  );

  // Handle status update (admin only) - memoized with useCallback
  const handleStatusUpdate = useCallback(() => {
    if (!newStatus || newStatus === ticket?.status) {
      return;
    }

    updateStatusMutation.mutate(
      { ticketId, status: newStatus },
      {
        onSuccess: () => {
          // Don't need to refetch - React Query will invalidate and refetch automatically
        },
      }
    );
  }, [newStatus, ticket?.status, updateStatusMutation, ticketId]);

  // Status options (admin only) - memoized to prevent re-creation
  const statusOptions = useMemo(
    () => [
      { value: "open", label: "Open" },
      { value: "in_progress", label: "In Progress" },
      { value: "resolved", label: "Resolved" },
      { value: "closed", label: "Closed" },
    ],
    []
  );

  return (
    <div
      className={
        isAdminRoute
          ? "min-h-screen bg-gray-50 dark:bg-gray-900"
          : "min-h-screen bg-gray-50 dark:bg-gray-900 py-8"
      }
    >
      <div
        className={
          isAdminRoute
            ? "max-w-7xl mx-auto py-6"
            : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        }
      >
        {/* Back Button */}
        <button
          onClick={() => navigate(isAdminRoute ? "/admin/tickets" : "/tickets")}
          className="mb-6 text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-2"
        >
          <span className="bi-arrow-left"></span>
          Back to Tickets
        </button>

        {/* Loading State */}
        {isLoading && <TicketDetailSkeleton />}

        {/* Error State */}
        {error && (
          <ErrorState
            message={error.message || "Failed to load ticket"}
            onRetry={refetch}
          />
        )}

        {/* Ticket Content */}
        {ticket && !isLoading && (
          <>
            {/* Ticket Header */}
            <Card className="mb-6 p-4 sm:p-6">
              <div className="space-y-4">
                {/* Mobile: Status Badge and Dropdown - Same line */}
                {/* Desktop: Title and Status Badge - justify-between */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  {/* Mobile: Status Badge and Dropdown */}
                  <div className="flex items-center gap-3 flex-wrap sm:hidden">
                    <StatusBadge
                      status={ticket.status}
                      className={getStatusColor(ticket.status)}
                      customLabels={{
                        open: "Open",
                        in_progress: "In Progress",
                        resolved: "Resolved",
                        closed: "Closed",
                      }}
                    />
                    {isAdmin && (
                      <FormSelect
                        value={newStatus || ticket?.status || "open"}
                        onChange={(e) => setNewStatus(e.target.value)}
                        disabled={updateStatusMutation.isPending}
                        className="w-full sm:w-40"
                        options={statusOptions}
                      />
                    )}
                  </div>

                  {/* Desktop: Title and Status Badge */}
                  <h1 className="hidden sm:block text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white sm:w-auto sm:flex-1">
                    {ticket.subject}
                  </h1>
                  <div className="hidden sm:flex items-center gap-3">
                    <StatusBadge
                      status={ticket.status}
                      className={getStatusColor(ticket.status)}
                      customLabels={{
                        open: "Open",
                        in_progress: "In Progress",
                        resolved: "Resolved",
                        closed: "Closed",
                      }}
                    />
                  </div>
                </div>

                {/* Mobile: Title - Full width */}
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white w-full sm:hidden">
                  {ticket.subject}
                </h1>

                {/* Mobile: Created Date/Time - Full width, one line */}
                {/* Desktop: Created and Updated dates with dropdown - justify-between */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 gap-2">
                    {/* Created Date/Time */}
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Created:</span>{" "}
                      {ticket.createdAt
                        ? formatDateLong(ticket.createdAt)
                        : "N/A"}
                    </div>

                    {/* Updated Date/Time */}
                    {ticket.updatedAt &&
                      ticket.updatedAt !== ticket.createdAt && (
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-medium">Updated:</span>{" "}
                          {formatDateLong(ticket.updatedAt)}
                        </div>
                      )}
                  </div>

                  {/* Desktop: Status Dropdown */}
                  {isAdmin && (
                    <div className="hidden sm:flex items-center gap-3">
                      <FormSelect
                        value={newStatus || ticket?.status || "open"}
                        onChange={(e) => setNewStatus(e.target.value)}
                        disabled={updateStatusMutation.isPending}
                        className="w-40"
                        options={statusOptions}
                      />
                      {newStatus !== ticket.status && (
                        <button
                          onClick={handleStatusUpdate}
                          disabled={updateStatusMutation.isPending}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                        >
                          {updateStatusMutation.isPending
                            ? "Updating..."
                            : "Update Status"}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Mobile: Status Dropdown (if admin) */}
                {isAdmin && newStatus !== ticket.status && (
                  <div className="sm:hidden">
                    <button
                      onClick={handleStatusUpdate}
                      disabled={updateStatusMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    >
                      {updateStatusMutation.isPending
                        ? "Updating..."
                        : "Update Status"}
                    </button>
                  </div>
                )}

                {/* Mobile: Customer - Full width */}
                {/* Desktop: Customer and Email - Same line */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <div>
                    <span className="font-medium">Customer:</span>{" "}
                    {ticket.customerName || ticket.customerEmail}
                  </div>
                  <div>
                    <span className="font-medium">Email:</span>{" "}
                    {ticket.customerEmail}
                  </div>
                </div>
              </div>
            </Card>

            {/* Messages */}
            <Card className="mb-6 p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Messages ({sortedMessages.length})
              </h2>
              <div className="space-y-4">
                {sortedMessages.map((message) => {
                  const isAdminMessage = message.senderRole === "admin";
                  const isCurrentUser = message.senderId === user?.id;

                  return (
                    <div
                      key={message.id}
                      className={`p-4 rounded-lg border ${
                        isAdminMessage
                          ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                          : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                      }`}
                    >
                      {/* Date and Time - Top on mobile, right on desktop */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                        <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 order-1 sm:order-2">
                          {formatDateLong(message.createdAt)}
                        </span>
                        {/* Name and Admin Badge - Below date on mobile, left on desktop */}
                        <div className="flex flex-row items-center gap-2 order-2 sm:order-1">
                          <span className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">
                            {message.senderName ||
                              (isAdminMessage ? "Admin" : "Customer")}
                          </span>
                          {isAdminMessage && (
                            <StatusBadge
                              status="admin"
                              className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 text-xs"
                              customLabels={{ admin: "Admin" }}
                            />
                          )}
                        </div>
                      </div>
                      {/* Message Text */}
                      <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {message.message}
                      </p>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Reply Form */}
            {ticket.status !== "closed" && ticket.status !== "resolved" && (
              <Card className="p-4 sm:p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {isAdmin ? "Reply to Customer" : "Add Reply"}
                </h2>
                <form onSubmit={handleReply} className="space-y-4">
                  <div>
                    <FormLabel htmlFor="reply" required>
                      Your Message
                    </FormLabel>
                    <FormTextarea
                      id="reply"
                      name="reply"
                      value={replyMessage}
                      onChange={(e) => {
                        setReplyMessage(e.target.value);
                        if (replyError) setReplyError("");
                      }}
                      placeholder="Type your reply here..."
                      rows={6}
                      required
                      disabled={replyMutation.isPending}
                      className={replyError ? "border-red-500" : ""}
                    />
                    {replyError && <FormError>{replyError}</FormError>}
                  </div>
                  <div className="flex items-center justify-end">
                    <button
                      type="submit"
                      disabled={replyMutation.isPending || !replyMessage.trim()}
                      className="px-6 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {replyMutation.isPending ? "Sending..." : "Send Reply"}
                    </button>
                  </div>
                </form>
              </Card>
            )}

            {/* Closed/Resolved Notice */}
            {(ticket.status === "closed" || ticket.status === "resolved") && (
              <Card className="bg-gray-50 dark:bg-gray-800 p-4 sm:p-6">
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                  This ticket is {ticket.status}. No further replies can be
                  added.
                </p>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Admin-specific content component that uses AdminLayout context
const AdminTicketDetailContent = () => {
  const { toggleSidebar } = useAdminLayout();

  return (
    <>
      <PageHeader
        title="Ticket Details"
        description="View and manage customer support ticket"
        toggleSidebar={toggleSidebar}
      />
      <TicketDetailContent />
    </>
  );
};

// Main component that wraps with AdminLayout if on admin route
export const TicketDetailPage = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin/tickets");

  // If admin route, wrap with AdminLayout
  if (isAdminRoute) {
    return (
      <AdminLayout>
        <AdminTicketDetailContent />
      </AdminLayout>
    );
  }

  // Otherwise, render without AdminLayout (customer view)
  return <TicketDetailContent />;
};
